// Lets people share files in the place and keeps them there: everyone can open or save what is
// shared or put it on the board, its sharer can remove it, and it stays after its sharer leaves.
//
// Shared files, through the SDK's Files: a file is named by its content's SHA-256, and the board
// notes who shared it, its name, type and size, or that it was removed (`file:{id}`). Every page
// fetches each file the board names and shares its copy too (`Files.publish`), so it opens and
// saves from it, others can fetch it from there, and a file stays after its sharer leaves. Each page
// publishes the IDs of the files it holds as the state `files`: a file is in the place while
// someone there holds it and the board names its sharer; once the board marks it removed, it
// leaves every list and every page stops sharing it.

import { Files, State, type Connection } from 'tablebox.io';
import { h, icon, iconButton, lang, nameDetail, panelHead, show, t } from './dom.ts';
import { formatSize } from './text.ts';

export const MAX_FILE_BYTES = 16 * 1024 * 1024;
/** How long a page waits to fetch again a file it could not fetch. */
const RETRY_MS = 2_000;
const SAVED = 'application/octet-stream';
/**
 * The media types a shared file opens as in a tab: those that run no script in Collab's origin, as
 * Element Web's blob allowlist (`getBlobSafeMimeType`), with PDF and plain text. A file of any
 * other type, HTML and SVG among them, is saved instead.
 */
const OPENS = new Set(['image/jpeg', 'image/gif', 'image/png', 'image/apng', 'image/webp', 'image/avif',
  'video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'audio/mp4', 'audio/webm', 'audio/aac', 'audio/mpeg', 'audio/ogg',
  'audio/wave', 'audio/wav', 'audio/x-wav', 'audio/x-pn-wav', 'audio/flac', 'audio/x-flac', 'application/pdf', 'text/plain']);
/** The type a shared file's bytes take in this page: its own when it opens in a tab, else one that browsers save. */
const safeType = (type: string) => {
  const essence = type.split(';')[0].trim().toLowerCase();
  return OPENS.has(essence) ? essence : SAVED;
};
/** Whether a shared file of `type` shows as a picture on the board: an image that opens in a tab (so not SVG). */
export const isImage = (type: string) => safeType(type).startsWith('image/');

export interface Meta {
  name: string;
  type: string;
  size: number;
}

/** What the board notes of a shared file: who shared it, its name, type and size, or that it was removed, and when. */
export type FileRecord = ({ by: string; at: number; deleted?: undefined } & Meta) | { deleted: true; at: number };

/** A file in the place: its ID (its content's SHA-256), what the board notes of it, and who shared it. */
interface Info extends Meta {
  id: string;
  by: string;
}

/** What the board uses of the shared files, each by its ID. */
export interface SharedFiles {
  /** Shares `file` and gives its ID and metadata; a file over 16 MiB is refused, with the status text. */
  share(file: File): Promise<{ id: string; meta: Meta } | undefined>;
  /** Whether the file `id` is in the place: listed, its sharer noted, and not removed. */
  has(id: string): boolean;
  /** A URL of the bytes of the file `id`, from this page's copy, kept while the file is in the place. */
  url(id: string): Promise<string>;
  /** Opens or saves the file `id`, as its row does. */
  open(id: string): void;
  /** The board's notes of the shared files, by ID: who shared each, and which were removed. */
  records(records: ReadonlyMap<string, FileRecord>): void;
}

/** This page's copy of a file: its bytes once fetched, the fetch while it runs, its share, and its URL once asked for. */
interface Copy {
  blob?: Blob;
  fetching?: Promise<Blob>;
  shared?: Files.Shared;
  url?: string;
}

/**
 * The Files panel: share files, list everyone's, open or save them, remove those this person shared, and put one on the board (`place`,
 * with its ID and metadata); `shared` notes on the board that this person shared a file, `remove` marks it
 * removed for everyone, and `changed` is told when the files listed or this page's copies change. `input` is the file input its
 * Share button opens, placed by the page outside the panel so that it is never inside a hidden ancestor.
 */
export function filesSection(connection: Connection, nameOf: (id: string) => string, place: (id: string, meta: Meta) => void, shared: (id: string, meta: Meta) => void, remove: (id: string) => void, changed: () => void): SharedFiles & { element: HTMLElement; input: HTMLInputElement; refresh: () => void; close: () => void } {
  let records: ReadonlyMap<string, FileRecord> = new Map();
  /** The IDs of the files this page holds, and everyone's. */
  const holding = State.publish<string[]>(connection, 'files', []);
  const held = State.subscribe<string[]>(connection, 'files', () => {
    render();
    changed();
  });
  const copies = new Map<string, Copy>();
  let retry: ReturnType<typeof setTimeout> | undefined;
  let closed = false;
  /** The names of sharers seen here, so that a file keeps its sharer's name after they leave. */
  const names = new Map<string, string>();
  const list = h('ul', { id: 'files' });
  const status = h('p', { class: 'muted', role: 'status' });

  // The label in the panel is the control the keyboard reaches; the input itself is not a tab stop.
  const input = h('input', { type: 'file', multiple: true, id: 'share-files', class: 'visually-hidden', tabindex: '-1', onchange: () => {
    for (const file of input.files ?? []) void share(file);
    input.value = '';
  } });
  const share = async (file: File) => {
    if (file.size > MAX_FILE_BYTES) {
      show(status, () => t('tooBig', { name: file.name }));
      return undefined;
    }
    const made = await Files.publish(connection, file);
    const id = made.ref.hash;
    // A file that finished hashing after the panel closed is not noted on the board.
    if (closed) return void made.close();
    const copy = copies.get(id) ?? {};
    if (copy.shared) made.close();
    else copy.shared = made;
    copies.set(id, { ...copy, blob: file });
    hold();
    const meta = { name: file.name, type: file.type || 'application/octet-stream', size: file.size };
    shared(id, meta);
    return { id, meta };
  };
  /** Publishes the IDs of the files this page holds. */
  const hold = () => {
    if (!closed) holding.set([...copies].filter(([, copy]) => copy.blob).map(([id]) => id).sort());
  };
  /** Whether someone there, this page too, holds the file `id`. */
  const present = (id: string) => copies.get(id)?.blob !== undefined || [...held.values()].some(ids => Array.isArray(ids) && ids.includes(id));

  /** The files in the place, as the board notes them: someone there holds each. */
  const listed = (): Info[] => [...records].flatMap(([id, record]) => (!record.deleted && present(id)
    ? [{ id, by: record.by, name: record.name, type: record.type, size: record.size }] : []));
  const infoOf = (id: string) => listed().find(info => info.id === id);

  /** This page's copy of the file `id`: fetched from someone who holds it, and shared from here too. */
  const keep = (info: Info): Promise<Blob> => {
    const copy = copies.get(info.id) ?? {};
    copies.set(info.id, copy);
    if (copy.blob) return Promise.resolve(copy.blob);
    copy.fetching ??= Files.fetch(connection, { hash: info.id, name: info.name, type: info.type, size: info.size }).then(async blob => {
      const made = await Files.publish(connection, blob);
      if (closed || copies.get(info.id) !== copy) {
        made.close();
        return blob;
      }
      copy.shared = made;
      copy.blob = blob;
      hold();
      render();
      changed();
      return blob;
    }).finally(() => (copy.fetching = undefined));
    return copy.fetching;
  };
  const forget = (id: string) => {
    const copy = copies.get(id);
    if (!copy) return;
    if (copy.url) URL.revokeObjectURL(copy.url);
    copy.shared?.close();
    copies.delete(id);
    hold();
  };
  /** A URL of the file's bytes, from this page's copy. */
  const urlOf = async (info: Info) => {
    try {
      const blob = await keep(info);
      const copy = copies.get(info.id);
      if (!copy) throw new Error(`${info.id}: not in the place`);
      copy.url ??= URL.createObjectURL(new Blob([blob], { type: safeType(info.type) }));
      return copy.url;
    } catch (error) {
      if (infoOf(info.id)) show(status, () => t('readFailed', { name: info.name }));
      throw error;
    }
  };
  const open = async (info: Info) => {
    if (safeType(info.type) === SAVED) return save(info);
    // Opened now, within the click, so that pop-up blockers let it through, and cut from this page
    // as `noopener` would (which returns no window to show the file in once it is read).
    const opened = window.open('', '_blank');
    if (opened) opened.opener = null;
    try {
      const url = await urlOf(info);
      if (opened) opened.location.href = url;
      else location.assign(url);
    } catch {
      opened?.close();
    }
  };
  const save = async (info: Info) => {
    const link = h('a', { href: await urlOf(info), download: info.name });
    document.body.append(link);
    link.click();
    link.remove();
  };
  const sharer = (id: string) => {
    const name = nameOf(id);
    if (name !== id) names.set(id, name);
    return names.get(id) ?? name;
  };

  /** Lists the files in the place, fetching each this page has no copy of, and lets go of those removed. */
  function render() {
    if (closed) return;
    for (const [id, record] of records) if (record.deleted) forget(id);
    const items = listed().sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
    for (const info of items) {
      if (copies.get(info.id)?.blob || copies.get(info.id)?.fetching) continue;
      keep(info).catch(() => (retry ??= setTimeout(() => {
        retry = undefined;
        render();
      }, RETRY_MS)));
    }
    show(list, items.length === 0 ? h('li', { class: 'empty' }, () => t('filesEmpty')) : items.map(info => {
      // The path the file was first shared at: the same on every page.
      const path = `files/${info.id}/${info.by}`;
      return h('li', { 'data-path': path, 'data-file': info.id, 'data-sharer': info.by, 'data-held': String(copies.get(info.id)?.blob !== undefined) }, icon('file'),
        nameDetail(info.name, () => t('sharedBy', { size: formatSize(info.size, lang), name: sharer(info.by) })),
        h('span', { class: 'actions' },
          iconButton({ class: 'place', onclick: () => place(info.id, info) }, 'pin', 'putOnBoard'),
          iconButton({ class: 'open', onclick: () => void open(info) }, 'open', 'open'),
          iconButton({ class: 'save', onclick: () => void save(info) }, 'save', 'save'),
          info.by === connection.id && iconButton({ class: 'remove', onclick: () => remove(info.id) }, 'close', 'removeFile')));
    }));
  }
  render();

  const element = h('section', { class: 'sheet files-panel', 'data-panel': 'files', hidden: true },
    panelHead(() => t('files'), h('label', { class: 'button primary', for: 'share-files', role: 'button', tabindex: '0', onkeydown: (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      input.click();
    } }, icon('paperclip'), () => t('shareFiles'))),
    h('div', { class: 'panel-body' }, status, list));
  return {
    element, input, share, refresh: render,
    close: () => {
      closed = true;
      clearTimeout(retry);
      held.close();
      holding.close();
      for (const copy of copies.values()) {
        copy.shared?.close();
        if (copy.url) URL.revokeObjectURL(copy.url);
      }
    },
    has: id => infoOf(id) !== undefined,
    url: id => {
      const info = infoOf(id);
      return info ? urlOf(info) : Promise.reject(new Error(`${id}: not in the place`));
    },
    open: id => {
      const info = infoOf(id);
      if (info) void open(info);
    },
    records: now => {
      records = now;
      render();
    },
  };
}
